import React, { useState, useEffect, useCallback, useContext } from 'react';
import './styles/VoucherPrintApprove.css';
import { jsPDF } from 'jspdf';
import axios from 'axios';
import { AuthContext } from '../AuthContext';
import { expenseType } from '../constants/constants';


const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const VoucherPrintApprove = () => {
  const [vouchers, setVouchers] = useState([]);
  const [filters, setFilters] = useState({
    voucher_number: '',
    year: '',
    before: '',
    after: '',
    party: '',
    ref_no: '',
    expense_type: '',
    payment_mode: '',
    approved_status: '',
  });

  const [editingVoucher, setEditingVoucher] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [customerNameOptions, setCustomerNameOptions] = useState([]);
  const [refNoOptions, setRefNoOptions] = useState([]);
  const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useContext(AuthContext);
  const username = user.username;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {

      if (filters?.before) {
        let response = await axios.get(BASE_URL + `voucher-entry?before=${filters?.before}`);
        setVouchers(response.data);
      } else if (filters?.after) {
        let response = await axios.get(BASE_URL + `voucher-entry?before=${filters?.after}`);
        setVouchers(response.data);
      } else if (filters?.voucher_number) {
        let response = await axios.get(BASE_URL + `voucher-entry?voucher_number=${filters?.voucher_number}`);
        setVouchers(response.data);
      } else if (filters?.party) {
        let response = await axios.get(BASE_URL + `voucher-entry?party=${filters?.party}`);
        setVouchers(response.data);
      } else if (filters?.year) {
        let response = await axios.get(BASE_URL + `voucher-entry?year=${filters?.year}`);
        setVouchers(response.data);
      } else if (filters?.ref_no) {
        let response = await axios.get(BASE_URL + `voucher-entry?ref_no=${filters?.ref_no}`);
        setVouchers(response.data);
      } else if (filters?.expense_type) {
        let response = await axios.get(BASE_URL + `voucher-entry?expense_type=${filters?.expense_type}`);
        setVouchers(response.data);
      } else if (filters?.approved_status) {
        let response = await axios.get(BASE_URL + `voucher-entry?approved_status=${filters?.approved_status}`);
        setVouchers(response.data);
      } else {
        let response = await axios.get(BASE_URL + `voucher-entry`);
        setVouchers(response.data);
      }

    } catch (error) {
      setError('Error fetching vouchers.');
      console.error('Error fetching vouchers:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const fetchCustomerNameOptions = useCallback(async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_BACKEND_URL + 'customer-names');
      setCustomerNameOptions(response.data);
    } catch (error) {
      console.error('Error fetching customer names:', error);
      setCustomerNameOptions([]);
    }
  }, []);

  const fetchRefNoOptions = useCallback(async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_BACKEND_URL + 'ref-no-options');
      setRefNoOptions(response.data);
    } catch (error) {
      console.error('Error fetching refNo options:', error);
      setRefNoOptions([]);
    }
  }, []);

  // const fetchExpenseTypeOptions = useCallback(async () => {
  //   try {
  //     const response = await axios.get(process.env.REACT_APP_BACKEND_URL + 'expense-types');
  //     setExpenseTypeOptions(response.data);
  //   } catch (error) {
  //     console.error('Error fetching expense types:', error);
  //     setExpenseTypeOptions([]);
  //   }
  // }, []);

  useEffect(() => {
    fetchCustomerNameOptions();
    fetchRefNoOptions();
  }, [fetchCustomerNameOptions, fetchRefNoOptions]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleEdit = (voucher) => {
    setEditingVoucher(voucher.voucher_number);
    setEditForm(voucher);
  };

  const handleEditFormChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      await axios.put(BASE_URL + `${editingVoucher}`, editForm);
      fetchVouchers();
      setEditingVoucher(null);
    } catch (error) {
      console.error('Error updating voucher:', error);
    }
  };

  const handleCancel = () => {
    setEditingVoucher(null);
  };

  const handleDelete = async (voucher_number) => {
    if (window.confirm('Are you sure you want to delete this voucher?')) {
      try {
        await axios.delete(BASE_URL + `voucher-entry/delete/${voucher_number}`);
        fetchVouchers();
      } catch (error) {
        console.error('Error deleting voucher:', error);
        alert(error.response.data.error);
      }
    }
  };

  const handleApproval = async (voucher_number, currentStatus) => {
    const newApprovalStatus = currentStatus === 'Approved' ? 'Unapproved' : 'Approved';

    const confirmMessage = currentStatus === 'Approved'
      ? 'Are you sure you want to unapprove this voucher?'
      : 'Are you sure you want to approve this voucher?';

    if (window.confirm(confirmMessage)) {
      try {
        await axios.put(process.env.REACT_APP_BACKEND_URL + 'voucher-entry/approval', {
          voucher_number,
          approvalStatus: newApprovalStatus,
          approved_by: username,
        });
        fetchVouchers();
      } catch (error) {
        console.error('Error updating approval status:', error);
      }
    }
  };

  const handlePrint = (voucher) => {
    if (!voucher) {
      console.error('Voucher data is missing');
      return;
    }

    const doc = new jsPDF();

    doc.text(`Voucher Number: ${voucher.voucher_number || ''}`, 10, 10);
    doc.text(`Date: ${formatTimestamp(voucher.date) || ''}`, 10, 20);
    doc.text(`Reference Number: ${voucher.ref_no || ''}`, 10, 30);
    doc.text(`Party: ${voucher.party || ''}`, 10, 40);
    doc.text(`Voucher Head: ${voucher.voucher_head || ''}`, 10, 50);
    doc.text(`Total Amount: ${voucher.total_amt || ''}`, 10, 60);
    doc.text(`Prepared By: ${voucher.prepared_by || ''}`, 10, 70);

    doc.save(`Voucher_${voucher.voucher_number}.pdf`);
  };
  return (
    <div className="w-full px-4 bg-gray-100">
      <h1 className="v-management-header">Voucher Approve and Print</h1>

      {/* Filters Section */}
      <div className="v-filters-section">
        <input
          type="text"
          name="voucher_number"
          className="v-filter-input"
          value={filters?.voucher_number}
          onChange={handleFilterChange}
          placeholder='Filter by voucher number'
        />
        <select name="year" className="v-filter-select" value={filters.year} onChange={handleFilterChange}>
          <option value="">Select Year</option>
          <option value="2021-2022">2021-2022</option>
          <option value="2022-2023">2022-2023</option>
          <option value="2023-2024">2023-2024</option>
        </select>
        <p className='mt-2 font-semibold'>Before date</p>

        <input
          type="date"
          name="before"
          className="v-filter-input"
          value={filters?.before}
          onChange={handleFilterChange}
          placeholder="Before date"
        />
        <p className='mt-2 font-semibold'>After date</p>
        <input
          type="date"
          name="after"
          className="v-filter-input"
          value={filters?.after}
          onChange={handleFilterChange}
          placeholder="After date"
        />

        <select name="customerName" className="v-filter-select" value={filters.customerName} onChange={handleFilterChange}>
          <option value="">Select Customer Name</option>
          {customerNameOptions.map((name, index) => (
            <option key={index} value={name}>{name}</option>
          ))}
        </select>

        <select name="refNo" className="v-filter-select" value={filters.refNo} onChange={handleFilterChange}>
          <option value="">Select Ref No</option>
          {refNoOptions.map((refNo, index) => (
            <option key={index} value={refNo}>{refNo}</option>
          ))}
        </select>

        <select name="expense_type" className="v-filter-select" value={filters.expense_type} onChange={handleFilterChange}>
          <option value="">Select Expense Type</option>
          {expenseType.map((type, index) => (
            <option key={index} value={type.name}>{type.name}</option>
          ))}
        </select>

        <select name="approved_status" className="v-filter-select" value={filters.approved_status} onChange={handleFilterChange}>
          <option>Select approved status</option>
          <option value="yes">Approved</option>
          <option value="no">Unapproved</option>
        </select>

        <button className="v-filter-button" onClick={fetchVouchers} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {error && <div className="v-error-message">{error}</div>}

      <table className="v-voucher-table">
        <thead className="v-voucher-table-head">
          <tr>
            <th>Voucher Number</th>
            <th>Date</th>
            <th>Ref No</th>
            <th>Party</th>
            <th>Voucher Head</th>
            <th>Expense Type</th>
            <th>Total Amount</th>
            <th>Payment mode</th>
            <th>Prepared By</th>
            <th>Approved By</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="v-voucher-table-body">
          {vouchers.map((voucher) => (
            <tr key={voucher.id}>
              {editingVoucher === voucher.voucher_number ? (
                <>
                  <td>{voucher.voucher_number}</td>
                  <td>
                    <input
                      type="date"
                      name="date"
                      className="v-edit-input"
                      value={formatTimestamp(editForm.date)}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      name="ref_no"
                      className="v-edit-input"
                      value={editForm.ref_no}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      name="party"
                      className="v-edit-input"
                      value={editForm.party}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      name="voucher_head"
                      className="v-edit-input"
                      value={editForm.voucher_head}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      name="expense_type"
                      className="v-edit-input"
                      value={editForm.expense_type}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      name="total_amt"
                      className="v-edit-input"
                      value={editForm.total_amt}
                      onChange={handleEditFormChange}
                    />
                  </td>
                  <td>{voucher.prepared_by}</td>
                  <td>{voucher.approved_by || 'N/A'}</td>
                  <td >
                    <div className="v-actions-buttons">
                      <button className="v-save-button" onClick={handleSave}>Save</button>
                      <button className="v-cancel-button" onClick={handleCancel}>Cancel</button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td>{voucher.voucher_number}</td>
                  <td>{new Date(voucher.date).toLocaleDateString()}</td>
                  <td>{voucher.ref_no}</td>
                  <td>{voucher.party}</td>
                  <td>{voucher.voucher_head}</td>
                  <td>{voucher.expense_type}</td>
                  <td>{voucher.total_amt}</td>
                  <td className={`font-bold ${voucher.payment_mode === 'cash' ? 'text-green-600' : 'text-blue-900'}`}>{voucher.payment_mode}</td>
                  <td>{voucher.prepared_by}</td>
                  <td>{voucher.approved_by || 'N/A'}</td>
                  <td >
                    <div className="v-actions-buttons">
                      {voucher.approval_status === 'Paid' ? (
                        <span className="v-paid-status">Paid</span>
                      ) : (
                        <>
                          <button className="v-edit-button" onClick={() => handleEdit(voucher)} disabled={voucher.approval_status === 'Approved'}>Edit</button>
                          <button className="v-delete-button" onClick={() => handleDelete(voucher.voucher_number)} disabled={voucher.approval_status === 'Approved'}>Delete</button>
                          <button
                            className="v-approve-button"
                            onClick={() => handleApproval(voucher.voucher_number, voucher.approval_status)}
                          >
                            {voucher.approval_status === 'Approved' ? 'Unapprove' : 'Approve'}
                          </button>
                          <button className="v-print-button" onClick={() => handlePrint(voucher)}>Print</button>
                        </>
                      )}
                    </div>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div >
  );
};

export default VoucherPrintApprove;
